import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Edit, History, Save, Loader2, Pen, Plus, AlertCircle, Check, Tag, Server, Shield } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faHistory } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import EditableField from '@/components/ui/EditableField';
import { generateAssetFieldsConfig } from './fieldsConfig';
import { cleanDateAndTimeObject, renderDate, toLocalDateString } from '../../../../shared/renderDate';
import './assetdetail.css';
import { useTheme } from '@/contexts/ThemeContext';
import { colorValues } from '@/contexts/ThemeContext';
import { getAsset, getAssetHistory, updateAsset, createAsset, deleteAsset } from '@/services/assetsService';
import { getRisk, putRisk } from '@/services/risksService';
import { syncDataTypeAssets } from '@/app/pages/data-retention-schedule/syncDataTypeAssetsUtil';
import { vendorScoreService, VendorScoreHistory } from '@/services/vendorScoreService';
import { AssetRegister } from '@/js/external-js/variableModules/namespaces/AssetRegister.namespace';

/**
 * Returns the component type, defaulting to 'Asset' if not specified
 * componentType should come from the database - we no longer infer from category
 * @param {string | undefined} componentType - The component type from the database
 * @returns {'Supplier' | 'Asset'} The component type
 */
const getComponentType = (componentType?: string): 'Supplier' | 'Asset' => {
  if (!componentType) return 'Asset';
  return componentType.toLowerCase() === 'supplier' ? 'Supplier' : 'Asset';
};

/**
 * Normalizes category names - converts legacy 'SaaS Software' to 'Software'
 */
const normalizeCategory = (category?: string): string => {
  if (!category) return 'Uncategorized';
  if (category.toLowerCase() === 'saas software') return 'Software';
  return category;
};

const extractHostnameFromUrl = (url?: string) => {
  if (!url) return '';
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return parsed.hostname;
  } catch (error) {
    return url;
  }
};

type UpguardHistoryRecord = Record<string, any>;

const mergeVendorScoreHistory = (
  existingHistory: UpguardHistoryRecord | null | undefined,
  vendorHistory: VendorScoreHistory[] | undefined,
  fallbackDomain: string
) => {
  const historyRecord: UpguardHistoryRecord = existingHistory ? { ...existingHistory } : { idHistory: 0 };
  let idHistory = typeof historyRecord.idHistory === 'number' ? historyRecord.idHistory : 0;

  const seenKeys = new Set<string>();
  let latestDate: Date | null = null;

  Object.entries(historyRecord).forEach(([key, value]) => {
    if (key === 'idHistory') return;
    if (!value || typeof value !== 'object') return;
    const normalizedDate = cleanDateAndTimeObject((value as any).checkDate);
    const isoDate = normalizedDate ? normalizedDate.toISOString() : ((value as any).checkDate ?? '');
    const domain = (value as any).domain || fallbackDomain;
    const scoreString = typeof (value as any).score === 'number' ? `${(value as any).score}` : ((value as any).score || '');

    historyRecord[key] = {
      ...(value as any),
      domain,
      checkDate: isoDate
    };

    if (isoDate && (!latestDate || (normalizedDate && normalizedDate > latestDate))) {
      latestDate = normalizedDate ?? latestDate;
    }

    const dedupeKey = `${isoDate}|${scoreString}|${domain}`;
    seenKeys.add(dedupeKey);
  });

  if (vendorHistory?.length) {
    vendorHistory.forEach((entry) => {
      const normalizedDate = cleanDateAndTimeObject(entry.captureDate);
      const isoDate = normalizedDate ? normalizedDate.toISOString() : '';
      const domain = entry.domain || fallbackDomain;
      const capturedScore = entry.capturedScore;
      const scoreString = typeof capturedScore === 'number'
        ? `${capturedScore}`
        : (capturedScore || '');

      const dedupeKey = `${isoDate}|${scoreString}|${domain}`;
      if (!isoDate || seenKeys.has(dedupeKey)) {
        return;
      }

      idHistory += 1;
      const newKey = String(idHistory);
      historyRecord[newKey] = {
        score: scoreString,
        checkDate: isoDate,
        domain
      };
      seenKeys.add(dedupeKey);

      if (normalizedDate && (!latestDate || normalizedDate > latestDate)) {
        latestDate = normalizedDate;
      }
    });
  }

  historyRecord.idHistory = idHistory;

  return {
    historyRecord,
    latestDate,
  };
};
import { useTenancy } from '@/contexts/TenancyContext';
import { toast } from 'sonner';
import { FirebaseTimestamp } from '@/js/external-js/config/main_config';
import { Button } from '@/components/ui/button';
import { Dialog, DialogHeader, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { Badge } from '@/components/ui/badge';
// Import types only to avoid loading the module on initial load
type VendorSecurityResult = {
  score: number | null;
  error: string | null;
  cached?: boolean; // Made optional to match the actual implementation
};

// Will be loaded dynamically when needed
const vendorSecurityUtils = {
  checkVendorSecurity: null as ((assetId: string, forceURL: string | undefined, bypassCache?: boolean, createAssetData?: any) => Promise<VendorSecurityResult>) | null,
  getSecurityScoreColor: null as ((score: number | null) => string) | null
};

import { VendorScoreChart } from '@/components/VendorScoreChart';
import type { Risk } from '@/types/risks';

// Define a local type for the asset to avoid import issues
// Local interface for the frontend asset model
interface AssetEntity {
  id: string;
  name: string;
  category: string;
  status: string;
  type: string;
  owner: string;
  criticality: string;
  risk: string;
  dataClassification: string;
  dataTypesShared: string[];
  dataStorageLocations: string[];
  subprocessor: string;
  publishInPrivacyPolicy: string;
  annualSecurityReviewCompleted: string;
  usesAI: string;
  vendorManagementNeeded: string;
  portable: string;
  dateAdded: any;
  serialNumber: string;
  label: string;
  physicalLabel: string;
  socCriticality: string;
  backupsRequired: 'Yes' | 'No'; // The UI field we want to display
  nextReviewDate: any;
  lastReviewDate: any;
  assetTeam: string;
  assetLocation: string;
  vendorWebsite: string;
  componentId: string;
  relatedControls: string[];
  legalObligations: string[];
  risks: string[];
  assetHistory?: Array<{
    changedBy: string;
    changedDate: any;
    changes: {
      [key: string]: {
        elementName: string;
        oldValue: any;
        newValue: any;
      };
    };
  }>;
  lastUpguardCheck?: Record<string, any> | null;
  upguardSecurityScoreHistory?: Record<string, any> | null;
}


// Helper to normalise "Yes"/"No" style flags that may come back in varying cases or as booleans
const normalizeYesNoString = (value: unknown, defaultValue: 'Yes' | 'No' = 'No'): 'Yes' | 'No' => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'yes' || normalized === 'true' || normalized === 'enabled') return 'Yes';
    if (normalized === 'no' || normalized === 'false' || normalized === 'disabled') return 'No';
  }
  return defaultValue;
};

// Map backend field names to frontend field names
const mapBackendToFrontendKey = (key: string) => {
  const mapping: Record<string, string> = {
    assetID: 'id',
    assetDesc: 'name',
    assetCat: 'category',
    status: 'status',
    componentType: 'type',
    assetOwner: 'owner',
    assetCriticality: 'criticality',
    assetRisk: 'risk',
    classification: 'dataClassification',
    dataTypesShared: 'dataTypesShared',
    dataStorageLocations: 'dataStorageLocations',
    assetSubprocessor: 'subprocessor',
    assetPublishInPrivacyPolicy: 'publishInPrivacyPolicy',
    securityReview: 'annualSecurityReviewCompleted',
    usesAI: 'usesAI',
    vendorManagementNeeded: 'vendorManagementNeeded',
    portability: 'portable',
    dateAdded: 'dateAdded',
    serial: 'serialNumber',
    label: 'label',
    physicalLabel: 'physicalLabel',
    socCriticality: 'socCriticality',
    backupNeeded: 'backupNeeded', // Keep as separate field
    backupsRequired: 'backupsRequired', // Keep as separate field
    nextReviewDate: 'nextReviewDate',
    lastReviewDate: 'lastReviewDate',
    assetTeam: 'assetTeam',
    assetLocation: 'assetLocation',
    vendorWebsite: 'vendorWebsite',
    componentId: 'componentId',
    relatedControls: 'relatedControls',
    assetRisks: 'risks',
  };
  return mapping[key] || key;
};

const mapFrontendToBackendKey = (key: string) => {
  const mapping: Record<string, string> = {
    id: 'assetID',
    name: 'assetDesc',
    category: 'assetCat',
    status: 'status',
    type: 'componentType',
    owner: 'assetOwner',
    criticality: 'assetCriticality',
    risk: 'assetRisk',
    dataClassification: 'classification',
    dataTypesShared: 'dataTypesShared',
    dataStorageLocations: 'dataStorageLocations',
    subprocessor: 'assetSubprocessor',
    publishInPrivacyPolicy: 'assetPublishInPrivacyPolicy',
    annualSecurityReviewCompleted: 'securityReview',
    usesAI: 'usesAI',
    vendorManagementNeeded: 'vendorManagementNeeded',
    portable: 'portability',
    dateAdded: 'dateAdded',
    serialNumber: 'serial',
    label: 'label',
    physicalLabel: 'physicalLabel',
    socCriticality: 'socCriticality',
    backupNeeded: 'backupNeeded', // Map directly
    backupsRequired: 'backupsRequired', // Map directly
    nextReviewDate: 'nextReviewDate',
    lastReviewDate: 'lastReviewDate',
    assetTeam: 'assetTeam',
    assetLocation: 'assetLocation',
    vendorWebsite: 'vendorWebsite',
    componentId: 'componentId',
    relatedControls: 'relatedControls',
    risks: 'assetRisks',
  };
  return mapping[key] || key;
};

// Map backend entity to frontend asset model for display
const mapAssetEntityToAssetDetail = (entity: AssetRegister.AssetEntity): AssetEntity => {
  console.log('[AssetDetail] Mapping entity to asset detail. backupNeeded from DB:', entity.backupNeeded);

  // Get component type from database, defaulting to 'Asset' if not set
  const componentType = getComponentType(entity.componentType);

  // Map the entity to our frontend model
  const mappedAsset: AssetEntity = {
    id: entity.assetID || '',
    name: entity.assetDesc || 'Unnamed Asset',
    category: normalizeCategory(entity.assetCat),
    status: entity.status || 'Active',
    type: componentType,
    owner: entity.assetOwner || 'Unassigned',
    criticality: entity.assetCriticality || 'Low',
    risk: entity.assetRisk || 'Low',
    dataClassification: entity.classification || 'Business Confidential',
    dataTypesShared: entity.dataTypesShared || [],
    dataStorageLocations: entity.dataStorageLocations || [],
    subprocessor: normalizeYesNoString(entity.assetSubprocessor),
    publishInPrivacyPolicy: normalizeYesNoString(entity.assetPublishInPrivacyPolicy),
    annualSecurityReviewCompleted: normalizeYesNoString(entity.securityReview),
    usesAI: normalizeYesNoString(entity.usesAI),
    vendorManagementNeeded: normalizeYesNoString(entity.vendorManagementNeeded),
    portable: normalizeYesNoString(entity.portability),
    dateAdded: entity.dateAdded || '',
    serialNumber: entity.serial || 'N/A',
    label: entity.label || 'No',
    physicalLabel: entity.physicalLabel || 'N/A',
    socCriticality: entity.socCriticality || 'N/A',
    // Use backupsRequired if it exists, otherwise fall back to backupNeeded for backward compatibility
    backupsRequired: normalizeYesNoString((entity as any).backupsRequired ?? entity.backupNeeded),
    nextReviewDate: entity.nextReviewDate || '',
    lastReviewDate: entity.lastReviewDate || '',
    assetTeam: entity.assetTeam || '',
    assetLocation: entity.assetLocation || '',
    vendorWebsite: entity.vendorWebsite || '',
    componentId: entity.componentId || `AST-${entity.assetID || ''}`,
    relatedControls: entity.relatedControls || [],
    legalObligations: (entity as any).legalObligations || [],
    risks: (entity as any).assetRisks || (entity as any).risks || [],
    assetHistory: entity.assetHistory || [],
    lastUpguardCheck: (entity as any).lastUpguardCheck || {},
    upguardSecurityScoreHistory: (entity as any).upguardSecurityScoreHistory || {}
  };

  console.log('[AssetDetail] Mapped backup field:', {
    backupNeeded: entity.backupNeeded,
    backupsRequired: mappedAsset.backupsRequired,
    fullEntity: entity
  });

  return mappedAsset;
};

interface AssetPageProps {
  isReadOnly?: boolean;
  onBackNavigation?: () => void;
}

const AssetPage: React.FC<AssetPageProps> = ({ isReadOnly = false, onBackNavigation }) => {
  const { users, activeTenancy, userIdToName } = useTenancy();
  const [isEditing, setIsEditing] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if this is a supplier page based on the URL path
  const isSupplierPage = location.pathname.toLowerCase().includes('supplier');

  // Helper function to navigate back to the appropriate register based on asset type and mode
  const navigateToAssetRegister = () => {
    // Use custom navigation handler if provided, otherwise determine path based on isReadOnly and asset type
    if (onBackNavigation) {
      onBackNavigation();
    } else {
      const isSupplier = asset?.type === 'Supplier';
      const basePath = isReadOnly ? '/auditor-view' : '';
      const targetPath = isSupplier ? `${basePath}/supplier-register` : `${basePath}/asset-register`;
      navigate(targetPath);
    }
  };
  const { id } = useParams<{ id: string }>();
  const [asset, setAsset] = useState<any>(null);
  const [assetHistory, setAssetHistory] = useState<AssetRegister.AssetHistoryItem[]>([]);
  const [originalAssetEntity, setOriginalAssetEntity] = useState<AssetRegister.AssetInstance | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<number[]>([]);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState<Record<string, any>>({});
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [activeField, setActiveField] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = React.useState(false);
  const [versionToRestore, setVersionToRestore] = React.useState<{ item: any; index: number } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [viewingHistoricalVersion, setViewingHistoricalVersion] = useState<boolean>(false);
  const [historicalAsset, setHistoricalAsset] = useState<AssetEntity | null>(null);
  const [isVendorScoreHistoryOpen, setIsVendorScoreHistoryOpen] = useState(false);
  const [securityScore, setSecurityScore] = useState<number | null>(null);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(false);
  const [securityCheckError, setSecurityCheckError] = useState<string | null>(null);
  const [isBannerSticky, setIsBannerSticky] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [vendorScoreHistory, setVendorScoreHistory] = useState<VendorScoreHistory[]>([]);
  const tenancyId = activeTenancy?.id || (window as any)?.companyID;

  const syncAssetRiskLinks = useCallback(async (assetId: string, previousRiskIds: string[], nextRiskIds: string[]) => {
    const companyId = activeTenancy?.id || (window as any)?.companyID;
    if (!companyId || !assetId) return;

    const normalize = (ids: string[]) => ids.filter(Boolean).map(id => id.toString());
    const prev = normalize(previousRiskIds);
    const next = normalize(nextRiskIds);

    const toAdd = next.filter(id => !prev.includes(id));
    const toRemove = prev.filter(id => !next.includes(id));

    if (!toAdd.length && !toRemove.length) return;

    const updateRiskAssets = async (riskId: string, mutation: 'add' | 'remove') => {
      try {
        const riskResponse = await getRisk(companyId, riskId);
        const riskData = ((riskResponse as any)?.entry ?? riskResponse) as Risk;
        const existingAssets = Array.isArray(riskData.assets) ? riskData.assets.map(String) : [];

        let updatedAssets = existingAssets;
        if (mutation === 'add') {
          if (existingAssets.includes(assetId)) return;
          updatedAssets = [...existingAssets, assetId];
        } else {
          if (!existingAssets.includes(assetId)) return;
          updatedAssets = existingAssets.filter(id => id !== assetId);
        }

        const updatedRisk: Risk = {
          ...riskData,
          assets: updatedAssets,
        };

        const riskIdentifier = (riskData as any).riskID ?? (riskData as any).id ?? riskId;
        await putRisk(companyId, riskIdentifier, updatedRisk);
      } catch (error) {
        console.error('[AssetDetail] Failed to sync asset-risk relationship', { riskId, mutation, error });
      }
    };

    await Promise.allSettled([
      ...toAdd.map(riskId => updateRiskAssets(riskId, 'add')),
      ...toRemove.map(riskId => updateRiskAssets(riskId, 'remove')),
    ]);
  }, [activeTenancy?.id]);

  const vendorHostname = useMemo(() => {
    if (!asset?.vendorWebsite) return '';
    try {
      const url = new URL(asset.vendorWebsite);
      return url.hostname;
    } catch (error) {
      return asset.vendorWebsite;
    }
  }, [asset?.vendorWebsite]);

  const extractUpguardDate = (value: any): Date | null => {
    if (!value) return null;
    if (typeof value === 'string') return cleanDateAndTimeObject(value);
    if (typeof value === 'object' && value.__time__) {
      return cleanDateAndTimeObject(value.__time__);
    }
    return cleanDateAndTimeObject(value);
  };

  const lastUpguardChecks = useMemo(() => {
    if (!asset || !asset.lastUpguardCheck || typeof asset.lastUpguardCheck !== 'object') {
      return [] as { normalizedDate: Date | null }[];
    }

    return Object.values(asset.lastUpguardCheck as Record<string, { lastChecked?: any }>)
      .filter((entry): entry is { lastChecked?: any } => !!entry && typeof entry === 'object')
      .map((entry) => ({
        normalizedDate: extractUpguardDate(entry.lastChecked),
      }));
  }, [asset?.lastUpguardCheck]);

  const latestUpguardCheckDate = useMemo(() => {
    const normalizedDates = lastUpguardChecks
      .map((entry) => entry.normalizedDate)
      .filter((date): date is Date => !!date && !isNaN(date.getTime()));

    if (normalizedDates.length === 0) return undefined;

    return new Date(Math.max(...normalizedDates.map((date) => date.getTime())));
  }, [lastUpguardChecks]);

  const upguardHistoryEntries = useMemo(() => {
    if (!asset || !asset.upguardSecurityScoreHistory || typeof asset.upguardSecurityScoreHistory !== 'object') {
      return [] as { key: string; normalizedDate: Date | null; displayDate: string; score: string; domain: string }[];
    }

    return Object.entries(asset.upguardSecurityScoreHistory as Record<string, { score?: string; checkDate?: any; domain?: string }>)
      .filter(([key, value]) => key !== 'idHistory' && !!value && typeof value === 'object')
      .map(([key, value]) => {
        const normalizedDate = extractUpguardDate(value?.checkDate);
        const displayDate = normalizedDate
          ? renderDate(normalizedDate)
          : value?.checkDate
            ? renderDate(value.checkDate)
            : '';
        const domainValue = value?.domain || vendorHostname || '';

        return {
          key,
          normalizedDate,
          displayDate,
          score: value?.score || '',
          domain: domainValue,
        };
      })
      .sort((a, b) => {
        const aTime = a.normalizedDate ? a.normalizedDate.getTime() : 0;
        const bTime = b.normalizedDate ? b.normalizedDate.getTime() : 0;
        return bTime - aTime;
      });
  }, [asset?.upguardSecurityScoreHistory, vendorHostname]);

  const latestHistoryEntry = useMemo(() => upguardHistoryEntries[0] ?? null, [upguardHistoryEntries]);

  const latestScoreData = useMemo(() => {
    if (latestHistoryEntry?.score) {
      const [scoreValue, maxValue] = latestHistoryEntry.score.split('/');
      const numericScore = Number.parseFloat(scoreValue);
      const numericMax = Number.parseFloat(maxValue);
      const normalized = Number.isFinite(numericScore) && Number.isFinite(numericMax) && numericMax > 0
        ? (numericScore / numericMax) * 100
        : Number.isFinite(numericScore)
          ? numericScore
          : null;

      return {
        display: latestHistoryEntry.score,
        normalized,
      };
    }

    if (typeof securityScore === 'number') {
      return {
        display: `${securityScore}/100`,
        normalized: securityScore,
      };
    }

    return {
      display: 'No score',
      normalized: null,
    };
  }, [latestHistoryEntry?.score, securityScore]);

  const latestCheckDisplay = latestHistoryEntry?.displayDate
    || (latestHistoryEntry?.normalizedDate ? renderDate(latestHistoryEntry.normalizedDate) : undefined)
    || (latestUpguardCheckDate ? renderDate(latestUpguardCheckDate) : 'N/A');

  const shouldShowVendorChart = useMemo(() => {
    if (!asset?.vendorWebsite) return false;
    const monitoringEnabled = typeof asset.vendorManagementNeeded === 'string'
      ? asset.vendorManagementNeeded.trim().toLowerCase() === 'yes' || asset.vendorManagementNeeded.trim().toLowerCase() === 'true'
      : asset.vendorManagementNeeded === true;
    return monitoringEnabled;
  }, [asset?.vendorWebsite, asset?.vendorManagementNeeded]);

  const monitoringDomain = useMemo(() => {
    if (latestHistoryEntry?.domain) return latestHistoryEntry.domain;
    if (vendorHostname) return vendorHostname;
    return 'Not available';
  }, [latestHistoryEntry?.domain, vendorHostname]);

  const fetchVendorScoreHistory = async () => {
    if (!asset?.vendorWebsite || !shouldShowVendorChart) return;

    try {
      const history = await vendorScoreService.getVendorScoreHistory(asset.vendorWebsite, 50);
      setVendorScoreHistory(history);
    } catch (error) {
      console.error('[AssetDetail] Failed to fetch vendor score history:', error);
      setVendorScoreHistory([]);
    }
  };

  useEffect(() => {
    if (shouldShowVendorChart && asset?.vendorWebsite) {
      fetchVendorScoreHistory();
    }
  }, [shouldShowVendorChart, asset?.vendorWebsite]);

  // ... rest of the code remains the same ...

  // Function to get security score color based on the score
  const getSecurityScoreColor = (score: number | null) => {
    if (score === null) return 'bg-gray-100 text-gray-800';
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  // Function to render security score badge
  const renderSecurityScore = () => {
    if (isCheckingSecurity) {
      return (
        <div className="inline-flex items-center ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Checking...
        </div>
      );
    }

    if (securityCheckError) {
      return (
        <div className="inline-flex items-center ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          {securityCheckError}
        </div>
      );
    }

    if (securityScore !== null) {
      return (
        <div className={`inline-flex items-center ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${getSecurityScoreColor(securityScore)}`}>
          <Shield className="h-3 w-3 mr-1" />
          Security Score: {securityScore}/100
        </div>
      );
    }

    return null;
  };



  // Function to check security score from Upguard
  const checkVendorSecurity = async (assetId?: string | undefined, forceURL: string | undefined) => {
    if (!forceURL) {
      console.error('[AssetDetail] URL is required');
      setSecurityScore(null);
      setSecurityCheckError('URL is required');
      return;
    }
    if (!assetId && !forceURL) {
      console.error('[AssetDetail] Asset ID or URL is required');
      setSecurityScore(null);
      setSecurityCheckError('Asset ID or URL is required');
      return;
    }

    console.log('[AssetDetail] Checking vendor security for asset ID:', assetId);
    console.log('[AssetDetail] Force URL:', forceURL);
    console.log('[AssetDetail] Is create mode:', isCreateMode);
    console.log('[AssetDetail] Asset:', asset);

    // Always fetch fresh asset data to ensure we have the latest
    let currentAsset = asset;
    console.log(`[AssetDetail] Fetching fresh asset data for ID: ${assetId}`);

    try {
      // 1. Fetch the latest asset data
      // skip this if in create mode
      let assetInstance;
      if (!isCreateMode) {
        assetInstance = await getAsset(currentAsset?.assetID || currentAsset?.id || assetId || currentAsset?.componentId);
        if (!assetInstance?.entity) {
          console.error('[AssetDetail@checkVendorSecurity] Invalid asset data received:', assetInstance);
          setSecurityCheckError('Failed to load asset data');
          return;
        }

        // 2. Map and update the asset state
        currentAsset = mapAssetEntityToAssetDetail(assetInstance.entity);
        setAsset(currentAsset);
      }

      // 3. Check if we have a vendor website to check
      if (!currentAsset.vendorWebsite && !forceURL) {
        console.log('[AssetDetail@checkVendorSecurity] No vendor website to check');
        setSecurityScore(null);
        setSecurityCheckError('No vendor website specified');
        return;
      }

      const url = forceURL || currentAsset?.vendorWebsite;

      // 4. Set loading state and clear any previous errors
      setIsCheckingSecurity(true);
      setSecurityCheckError(null);

      console.log('[AssetDetail] Checking vendor security for asset ID:', assetId);

      // 5. Dynamically import the vendor security utilities only when needed
      if (!vendorSecurityUtils.checkVendorSecurity || !vendorSecurityUtils.getSecurityScoreColor) {
        console.log('[AssetDetail] Lazy loading vendor security utilities');
        try {
          const vendorSecurity = await import('@/utils/vendorSecurity');
          vendorSecurityUtils.checkVendorSecurity = vendorSecurity.checkVendorSecurity;
          vendorSecurityUtils.getSecurityScoreColor = vendorSecurity.getSecurityScoreColor;
        } catch (importError) {
          console.error('[AssetDetail] Failed to load vendor security utilities:', importError);
          setSecurityCheckError('Failed to load security tools');
          return;
        }
      }

      // Use the shared vendorSecurity utility which handles caching and domain tracking
      // We pass the asset ID to the utility, which will handle the vendor website lookup
      if (!vendorSecurityUtils.checkVendorSecurity) {
        console.error('[AssetDetail] Vendor security utility not initialized');
        setSecurityCheckError('Security tools not initialized');
        return;
      }

      const { score, error, cached } = await vendorSecurityUtils.checkVendorSecurity(assetId, forceURL || currentAsset.vendorWebsite, true, !isCreateMode ? currentAsset : undefined);

      if (error) {
        console.error('[AssetDetail] Error checking vendor security:', error);
        setSecurityCheckError(error);
        return;
      }

      if (score !== null) {
        // Extract domain from URL for the cache key
        let domain: string;
        try {
          // if domain doesnt have http or https but does have a period, skip the security check
          if (!url.includes('http://') && !url.includes('https://') && url.includes('.')) {
            domain = url;
          } else {
            const urlObj = new URL(url);
            domain = urlObj.hostname;
          }
        } catch (e) {
          console.error('[AssetDetail] Invalid URL:', url);
          setSecurityCheckError('Invalid vendor website URL');
          return;
        }

        // Get the current history or initialize if it doesn't exist
        const latestHistory = (await getAsset(assetId || currentAsset.id))?.entity?.upguardSecurityScoreHistory
          || (asset && asset.upguardSecurityScoreHistory)
          || { idHistory: 0 };
        const currentHistory = { ...latestHistory };
        const newIdHistory = (currentHistory.idHistory || 0) + 1;

        // Prepare the updates including history
        const updates = {
          securityScore: score,
          lastUpguardCheck: {
            ...((asset && asset.lastUpguardCheck) || {}),
            [domain]: {
              lastChecked: new Date().toISOString()
            }
          },
          upguardSecurityScoreHistory: {
            ...currentHistory,
            [newIdHistory]: {
              score: `${score}/950`,
              checkDate: new Date().toISOString(),
              domain: domain
            },
            idHistory: newIdHistory
          }
        };

        // Update local state with the new score and history
        setAsset(prev => {
          if (!prev) return prev; // Skip update if previous state is null
          const mergedHistory = {
            ...currentHistory,
            [newIdHistory]: {
              score: `${score}/950`,
              checkDate: new Date().toISOString(),
              domain,
            },
            idHistory: newIdHistory,
          };
          return {
            ...prev,
            ...updates,
            lastUpguardCheck: {
              ...((prev.lastUpguardCheck || {})),
              [domain]: {
                lastChecked: new Date().toISOString()
              }
            },
            upguardSecurityScoreHistory: mergedHistory,
          };
        });

        // If we're editing, update the changes object and backend
        if (isEditing) {
          // Update local changes
          setChanges(prev => ({
            ...(prev || {}),
            ...updates
          }));

          // Update backend
          updateAsset(assetId, updates)
            .then(response => {
              console.log('[AssetDetail] Security score and history updated in backend:', response);
            })
            .catch(error => {
              console.error('[AssetDetail] Error updating security score in backend:', error);
            });
        }

        setSecurityScore(score);
      }
    } catch (error) {
      console.error('Error checking vendor security:', error);
      setSecurityCheckError('Failed to check vendor security');
    } finally {
      setIsCheckingSecurity(false);
    }
  };


  // Set up scroll listener and measure banner height
  useEffect(() => {
    if (!bannerRef.current) return;

    // Measure banner height
    setBannerHeight(bannerRef.current.offsetHeight);

    const handleScroll = () => {
      if (!bannerRef.current) return;

      const scrollPosition = window.scrollY;
      const bannerPosition = bannerRef.current.offsetTop;
      const bannerHeight = bannerRef.current.offsetHeight;

      // Make banner sticky when scrolled past its original position
      setIsBannerSticky(scrollPosition > bannerPosition);
    };

    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    // Initial check
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [viewingHistoricalVersion]);

  // Handle version selection
  const handleVersionSelect = (index: number) => {
    console.log('handleVersionSelect called with index:', index);
    console.log('Current assetHistory:', assetHistory);

    setSelectedVersion(index);
    if (index !== null) {
      // Start with the original asset state (deep copy)
      const versionedAsset = JSON.parse(JSON.stringify(asset));
      const sortedHistory = assetHistory.sort((a, b) => (cleanDateAndTimeObject(b.changedDate)?.getTime() || 0) - (cleanDateAndTimeObject(a.changedDate)?.getTime() || 0));
      // Get all history items up to and including the selected version
      const relevantHistory = sortedHistory.slice(0, index + 1);

      // Track the state of each field as we apply changes
      const fieldStates: Record<string, any> = {};

      // Apply each change in chronological order
      relevantHistory.forEach((historyItem, i) => {
        if (!historyItem?.changes) return;

        console.log(`Processing history item ${i}:`, historyItem.changes);

        // Apply each change from this history item
        const isSelectedVersion = (i === index);
        Object.entries(historyItem.changes).forEach(([key, change]) => {
          if (change && typeof change === 'object' && 'newValue' in change && 'oldValue' in change) {
            console.log(`  Updating ${key} from ${JSON.stringify(!isSelectedVersion ? change.newValue : change.oldValue)} to:`, isSelectedVersion ? change.newValue : change.oldValue);
            // Update our tracked state
            const value = isSelectedVersion ? change.newValue : change.oldValue;
            console.log(`  Setting ${key} to:`, value);
            fieldStates[key] = value;
            console.log(`  Current fieldStates:`, fieldStates);
          }
        });
      });

      // Remap field names to match frontend model
      Object.entries(fieldStates).forEach(([key, value]) => {
        // Map backend field names to frontend field names
        const frontendKey = mapBackendToFrontendKey(key);
        versionedAsset[frontendKey] = value;
      });

      console.log('Final versionedAsset state:', versionedAsset);
      setHistoricalAsset(versionedAsset);
      setViewingHistoricalVersion(true);
      console.log('Historical view enabled for version', index);
    } else {
      console.log('Exiting historical view');
      setViewingHistoricalVersion(false);
      setHistoricalAsset(null);
    }
  };

  const exitHistoricalView = () => {
    setViewingHistoricalVersion(false);
    setHistoricalAsset(null);
    setSelectedVersion(null);
  };

  // Determine which asset to display - current or historical version
  const displayAsset = viewingHistoricalVersion && historicalAsset ? historicalAsset : asset;

  // Controls linked to this asset
  const assetControlIds = useMemo<string[]>(() => {
    const rel: unknown[] = Array.isArray(asset?.relatedControls) ? asset.relatedControls : [];
    return Array.from(new Set(rel.filter(Boolean).map((x) => String(x))));
  }, [JSON.stringify(asset?.relatedControls || [])]);

  // Helper to add MCM control to asset's relatedControls
  const addMcmControlToAsset = useCallback(async (controlId: string) => {
    if (!asset || !id || isCreateMode) return;

    const currentRelated = Array.isArray(asset?.relatedControls)
      ? asset.relatedControls
      : [];

    if (currentRelated.includes(controlId)) {
      return;
    }

    const updatedRelated = [...currentRelated, controlId];

    try {
      await updateAsset(id, { relatedControls: updatedRelated });
      setAsset(prev => ({
        ...(prev as any),
        relatedControls: updatedRelated,
      }));
    } catch (e) {
      console.warn('[AssetDetail] Failed to add control to relatedControls', e);
    }
  }, [asset, id, isCreateMode]);

  // Generate field config with user data
  // DED-1447: Pass customer plan to filter out fields for Starter tenancies
  const assetFieldsConfig = generateAssetFieldsConfig(users || [], activeTenancy?.plan);

  // Auto-commit any open field edits before saving the whole asset
  const commitActiveField = () => {
    // If there's an active field, commit its value
    if (activeField && asset) {
      // Capture the current value of the active field
      const inputElement = document.querySelector(`[data-field-key="${activeField}"] input`) as HTMLInputElement;
      const selectElement = document.querySelector(`[data-field-key="${activeField}"] select`) as HTMLSelectElement;
      const textareaElement = document.querySelector(`[data-field-key="${activeField}"] textarea`) as HTMLTextAreaElement;

      const activeFieldValue = inputElement?.value || selectElement?.value || textareaElement?.value;

      if (activeFieldValue) {
        // Update our local state with this value
        setAsset(prev => ({
          ...prev,
          [activeField]: activeFieldValue
        }));

        // Add to changes
        setChanges(prev => ({
          ...(prev || {}),
          [activeField]: activeFieldValue
        }));
      }

      // Close the active field
      setActiveField(null);
    }
  };

  // Load asset data function - handles both new and existing assets
  const loadAssetData = async () => {
    if (!id) {
      toast.error('No asset ID provided');
      navigate('/asset-register');
      return;
    }

    try {
      setLoading(true);

      // Check if we're in create mode
      if (id === 'new') {
        // Determine component type based on URL path
        const componentType = isSupplierPage ? 'Supplier' : 'Asset';
        console.log(`Creating new ${componentType} (detected from URL: ${location.pathname})`);
        setIsCreateMode(true);
        setIsEditing(true); // Automatically start in editing mode

        // Initialize with a new empty asset with default values
        // componentType is set based on whether user navigated from Asset or Supplier register
        const newAsset = {
          id: '', // Will be assigned by the system
          name: '',
          category: 'Software',
          status: 'Active',
          type: 'Software', // Force Asset or Supplier based on the page they came from
          componentType,
          owner: '', // Default to empty, user must select
          criticality: 'Low',
          risk: 'Low',
          dataClassification: 'Business Confidential',
          dataTypesShared: [],
          dataStorageLocations: [],
          subprocessor: 'No',
          publishInPrivacyPolicy: 'No',
          usesAI: 'No',
          annualSecurityReviewCompleted: 'No',
          vendorManagementNeeded: 'No',
          portable: 'No',
          dateAdded: cleanDateAndTimeObject(new Date()),
          serialNumber: '',
          label: 'No',
          socCriticality: 'N/A',
          backupsRequired: 'No',
          nextReviewDate: cleanDateAndTimeObject(new Date(Date.now() + 364 * 24 * 60 * 60 * 1000)), // Default to one year from now
          lastReviewDate: cleanDateAndTimeObject(new Date()),
          assetTeam: '',
          assetLocation: '',
          vendorWebsite: '',
          componentId: '',
          relatedControls: [],
          physicalLabel: '',
          legalObligations: [],
          risks: [],
          lastUpguardCheck: {},
          upguardSecurityScoreHistory: { idHistory: 0 },
        };

        setAsset(newAsset);
        setAssetHistory([]);
        setLoading(false);
        return;
      }

      // Normal asset loading for existing assets
      const assetInstance = await getAsset(id);

      if (!assetInstance) {
        toast.error('Asset not found');
        navigate('/asset-register');
        return;
      }

      // Store the original entity for later use
      setOriginalAssetEntity(assetInstance);
      setIsCreateMode(false);

      // Map to frontend model
      const mappedAsset = mapAssetEntityToAssetDetail(assetInstance.entity);
      let augmentedAsset = { ...mappedAsset };

      if (mappedAsset.vendorWebsite) {
        try {
          const vendorHistory = await vendorScoreService.getVendorScoreHistory(mappedAsset.vendorWebsite, 50);
          if (vendorHistory?.length) {
            const hostname = extractHostnameFromUrl(mappedAsset.vendorWebsite);
            const { historyRecord, latestDate } = mergeVendorScoreHistory(
              mappedAsset.upguardSecurityScoreHistory as UpguardHistoryRecord,
              vendorHistory,
              hostname
            );

            augmentedAsset = {
              ...augmentedAsset,
              upguardSecurityScoreHistory: historyRecord,
              lastUpguardCheck: {
                ...(mappedAsset.lastUpguardCheck || {}),
                ...(latestDate
                  ? {
                    [hostname]: {
                      lastChecked: latestDate.toISOString(),
                    },
                  }
                  : {}),
              },
            };
          }
        } catch (historyError) {
          console.error('[AssetDetail] Failed to fetch vendor score history:', historyError);
        }
      }

      setAsset(augmentedAsset);

      // Check vendor security on initial load if vendor website exists and we have an asset ID
      if (augmentedAsset.vendorWebsite && id) {
        console.log('[AssetDetail] Initial load - checking vendor security for asset:', id, 'with website:', augmentedAsset.vendorWebsite);
        // Pass the asset ID to the checkVendorSecurity function
        checkVendorSecurity(id, augmentedAsset.vendorWebsite).catch(error => {
          console.error('[AssetDetail] Error checking vendor security on initial load:', error);
        });
      } else {
        console.log('[AssetDetail] Skipping vendor security check - missing vendor website or asset ID', {
          hasVendorWebsite: !!augmentedAsset.vendorWebsite,
          hasAssetId: !!id
        });
      }

      // Fetch history
      const history = await getAssetHistory(id);
      // Sort history by date (newest first)
      const sortedHistory = [...history].sort((a, b) => {
        return new Date(cleanDateAndTimeObject(b.changedDate)).getTime() - new Date(cleanDateAndTimeObject(a.changedDate)).getTime();
      });
      setAssetHistory(sortedHistory);
    } catch (error) {
      console.error('Error fetching asset:', error);
      toast.error('Failed to load asset details');
    } finally {
      setLoading(false);
    }
  };

  // Load asset history
  const loadAssetHistory = async () => {
    if (!id || id === 'new') return;

    try {
      const history = await getAssetHistory(id);
      // Sort history by date (newest first)
      const sortedHistory = [...history].sort((a, b) => {
        console.log(cleanDateAndTimeObject(a.changedDate), cleanDateAndTimeObject(b.changedDate));
        return new Date(cleanDateAndTimeObject(b.changedDate)).getTime() - new Date(cleanDateAndTimeObject(a.changedDate)).getTime();
      });
      setAssetHistory(sortedHistory);
    } catch (error) {
      console.error('Error loading asset history:', error);
      toast.error('Failed to load asset history');
    }
  };

  // Restore a previous version of the asset
  const handleRestoreClick = (item: any, index: number) => {
    setVersionToRestore({ item, index });
    setShowRestoreDialog(true);
  };

  const handleRestoreVersion = async () => {
    if (!versionToRestore) return;

    const { item: historyItem, index } = versionToRestore;
    if (!id || !window.confirm('Are you sure you want to restore this version? This will overwrite the current asset data.')) {
      return;
    }

    try {
      setIsRestoring(true);

      // Get the history up to and including the selected version
      const sortedHistory = [...assetHistory].sort((a, b) =>
        (cleanDateAndTimeObject(a.changedDate)?.getTime() || 0) -
        (cleanDateAndTimeObject(b.changedDate)?.getTime() || 0)
      );

      // Get all history items up to and including the selected version
      const relevantHistory = sortedHistory.slice(0, index + 1);

      // Start with a clean slate of the current asset
      const restoredAsset = JSON.parse(JSON.stringify(asset));

      // Track the state of each field as we apply changes
      const fieldStates: Record<string, any> = {};

      // Apply each change in chronological order
      relevantHistory.forEach((item) => {
        if (!item?.changes) return;

        // For each change in this history item
        Object.entries(item.changes).forEach(([key, change]) => {
          if (change && typeof change === 'object' && 'newValue' in change) {
            // For the selected version, we want to use newValue
            // For previous versions, we use oldValue
            const valueToUse = item === historyItem ? change.newValue : change.oldValue;
            fieldStates[key] = valueToUse;
          }
        });
      });

      // Create the update object using backend field names
      const backendUpdate: Record<string, any> = {};

      // Map frontend field names to backend field names
      Object.entries(fieldStates).forEach(([key, value]) => {
        // Map frontend field names to backend field names
        const backendKey = mapFrontendToBackendKey(key);
        backendUpdate[backendKey] = value;
      });

      // Add restoration note
      // backendUpdate.notes = `Restored from version ${renderDate(historyItem.changedDate, { dateStyle: 'long' })}`;

      // Clean the update object - remove any unwanted properties
      const cleanBackendUpdate = Object.keys(backendUpdate).reduce((acc, key) => {
        // Skip any numeric string keys (like "0") and undefined/null values
        if (!/^\d+$/.test(key) && backendUpdate[key] !== undefined && backendUpdate[key] !== null) {
          acc[key] = backendUpdate[key];
        }
        return acc;
      }, {} as Record<string, any>);

      console.log('Restoring asset with data:', cleanBackendUpdate);

      // Update the asset with the cleaned data
      await updateAsset(id, cleanBackendUpdate);

      // Reload the asset to show the restored data
      await loadAssetData();

      // Exit historical view if active
      setViewingHistoricalVersion(false);
      setSelectedVersion(null);

      toast.success('Asset version restored successfully');
    } catch (error) {
      console.error('Error restoring asset version:', error);
      toast.error('Failed to restore asset version');
    } finally {
      setIsRestoring(false);
    }
  };

  // Load asset data when component mounts or ID changes
  useEffect(() => {
    if (!activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === 'Loading...') return;
    loadAssetData();
    loadAssetHistory();
  }, [id, navigate, activeTenancy.id]);

  // Handle saving changes to the asset
  const renderHistoryIcon = (changeType: string) => {
    switch (changeType) {
      case 'created':
        return <Plus size={16} className="text-green-500 dark:text-green-400" />;
      case 'status':
        return <Tag size={16} className="text-blue-500 dark:text-blue-400" />;
      case 'criticality':
        return <AlertCircle size={16} className="text-orange-500 dark:text-orange-400" />;
      case 'owner':
        return <Check size={16} className="text-purple-500 dark:text-purple-400" />;
      case 'type':
        return <Server size={16} className="text-indigo-500 dark:text-indigo-400" />;
      case 'security':
        return <Shield size={16} className="text-red-500 dark:text-red-400" />;
      default:
        return <Pen size={16} className="text-blue-500 dark:text-blue-400" />;
    }
  };

  // Detect change type based on field name
  const getChangeType = (fieldName: string) => {
    if (fieldName.toLowerCase().includes('create') || fieldName.toLowerCase().includes('add')) {
      return 'created';
    } else if (fieldName.toLowerCase().includes('status')) {
      return 'status';
    } else if (fieldName.toLowerCase().includes('critic') || fieldName.toLowerCase().includes('risk')) {
      return 'criticality';
    } else if (fieldName.toLowerCase().includes('owner')) {
      return 'owner';
    } else if (fieldName.toLowerCase().includes('type')) {
      return 'type';
    } else if (fieldName.toLowerCase().includes('secur') || fieldName.toLowerCase().includes('review')) {
      return 'security';
    }
    return 'default';
  };

  useEffect(() => {
    if (showHistory && historyPanelRef.current) {
      historyPanelRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showHistory]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteAsset(id);
      toast.success('Asset deleted successfully');
      navigate('/asset-register');
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error('Failed to delete asset');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    console.log('[AssetDetail] Save initiated. Current changes:', {
      changes,
      currentAsset: asset,
      backupsRequired: asset?.backupsRequired,
      backupNeeded: originalAssetEntity?.backupNeeded
    });

    try {
      setSaving(true);

      // First, commit any active field edits
      commitActiveField();

      if (isCreateMode) {
        // We're creating a new asset
        console.log('Creating new asset...');

        if (!asset) {
          toast.error('No asset data to save');
          return;
        }

        // In create mode, directly save all field values
        if (isCreateMode) {
          console.log('In create mode, invoking save handlers for all fields...');

          // Process all fields that need saving
          assetFieldsConfig.forEach(field => {
            // Skip fields with no value or unchanged fields
            if (!asset[field.key]) return;

            try {
              console.log(`Auto-saving field: ${field.key} = ${asset[field.key]}`);

              // This mimics the onSave handler in the EditableField component
              // It updates both the asset state and the changes tracking
              setAsset(prev => ({
                ...prev,
                [field.key]: asset[field.key]
              }));

              // Track the change
              setChanges(prev => ({
                ...(prev || {}),
                [field.key]: asset[field.key]
              }));

            } catch (error) {
              console.error(`Error auto-saving field ${field.key}:`, error);
            }
          });

          console.log('All fields processed for auto-save');
        }

        // Prepare the asset data for backend (map frontend to backend model)
        const backendAsset: Partial<AssetRegister.AssetEntity> = {
          assetDesc: asset.name || 'Unnamed Asset',
          assetCat: asset.category || 'Software',
          status: asset.status || 'Active',
          // assetCat: asset.type || 'Software',
          componentType: (isSupplierPage ? 'Supplier' : 'Asset'),
          assetOwner: asset.owner || '',
          assetCriticality: asset.criticality || 'Low',
          assetRisk: asset.risk || 'Low',
          classification: asset.dataClassification || 'Business Confidential',
          dataTypesShared: asset.dataTypesShared || [],
          dataStorageLocations: asset.dataStorageLocations || [],
          assetSubprocessor: asset.subprocessor || 'No',
          assetPublishInPrivacyPolicy: asset.publishInPrivacyPolicy || 'No',
          usesAI: asset.usesAI || 'No',
          securityReview: asset.annualSecurityReviewCompleted || 'No',
          vendorManagementNeeded: asset.vendorManagementNeeded || 'No',
          portability: asset.portable || 'No',
          dateAdded: asset.dateAdded ? new Date(asset.dateAdded) : new Date(),
          serial: asset.serialNumber || '',
          label: asset.label || 'No',
          physicalLabel: asset.physicalLabel || 'N/A',
          socCriticality: asset.socCriticality || 'N/A',
          // Set backupNeeded from the backupsRequired UI field
          backupNeeded: asset.backupsRequired || 'No',
          nextReviewDate: asset.nextReviewDate ? asset.nextReviewDate : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
          lastReviewDate: asset.lastReviewDate ? asset.lastReviewDate : null,
          assetTeam: asset.assetTeam || '',
          assetLocation: asset.assetLocation || '',
          vendorWebsite: asset.vendorWebsite || '',
          relatedControls: asset.relatedControls || [],
          legalObligations: asset.legalObligations || [],
          risks: Array.isArray(asset.risks) ? asset.risks : [],
          lastUpguardCheck: {},
          upguardSecurityScoreHistory: { idHistory: 0 },
        };

        console.log('[AssetDetail] Creating new asset with backup fields:', {
          frontendBackupsRequired: asset.backupsRequired,
          backendBackupNeeded: backendAsset.backupNeeded,
          fullBackendAsset: backendAsset
        });

        // Create the new asset
        const result = await createAsset(backendAsset);

        if (!result) {
          toast.error('Failed to create asset');
          setSaving(false);
          return;
        }

        // Show success message
        toast.success('Asset created successfully');

        // Sync asset-risk relationships
        try {
          await syncAssetRiskLinks(result.id, [], asset.risks);
          toast.success('Asset-risk relationships synced successfully');
        } catch (error) {
          toast.error('Failed to sync asset-risk relationships');
        }


        // Reset creation state
        setIsEditing(false);
        setActiveField(null);

        // Navigate to the newly created asset/supplier based on the page type
        navigate(isSupplierPage ? `/supplierdetail/${result.id}` : `/assetdetail/${result.id}`);
        return;
      } else {
        // We're updating an existing asset
        if (!id || !changes || !originalAssetEntity) {
          toast.error('Missing data for update');
          return;
        }

        // Prepare changes for backend, ensuring backupsRequired is mapped to backupNeeded
        const backendChanges: Partial<AssetRegister.AssetEntity> = {};

        // Map frontend fields to backend fields
        Object.entries(changes).forEach(([key, value]) => {
          const backendKey = mapFrontendToBackendKey(key);
          // Special handling for backupsRequired -> backupNeeded mapping
          if (key === 'backupsRequired') {
            backendChanges.backupNeeded = value;
          } else {
            backendChanges[backendKey] = value;
          }
        });

        // legalObligations will be included via the changes mapping when edited

        console.log('[AssetDetail] Updating asset with changes:', {
          frontendChanges: changes,
          backendChanges,
          currentBackupsRequired: asset.backupsRequired,
          currentBackupNeeded: originalAssetEntity?.backupNeeded
        });

        // Update asset with mapped changes
        await updateAsset(id, backendChanges);

        // Sync asset-risk relationships
        try {
          await syncAssetRiskLinks(id, originalAssetEntity?.risks || [], asset.risks);
          toast.success('Asset-risk relationships synced successfully');
        } catch (error) {
          toast.error('Failed to sync asset-risk relationships');
        }

        // Refresh asset data
        const assetInstance = await getAsset(id);

        if (assetInstance) {
          setOriginalAssetEntity(assetInstance);
          const mappedAsset = mapAssetEntityToAssetDetail(assetInstance.entity);
          console.log('[AssetDetail] After update, mapped asset backup fields:', {
            mappedBackupsRequired: mappedAsset.backupsRequired,
            entityBackupNeeded: assetInstance.entity.backupNeeded,
            fullMappedAsset: mappedAsset
          });
          setAsset(mappedAsset);

          // Refresh history
          const history = await getAssetHistory(id);
          // Sort history by date (newest first)
          const sortedHistory = [...history].sort((a, b) => {
            return new Date(cleanDateAndTimeObject(b.changedDate)).getTime() - new Date(cleanDateAndTimeObject(a.changedDate)).getTime();
          });
          setAssetHistory(sortedHistory);
        }

        // Clear changes and exit edit mode
        setChanges(undefined);
        setIsEditing(false);

        // If vendor website was changed, check security score
        if (changes?.vendorWebsite) {
          console.log('[AssetDetail] Vendor website changed, checking security score...');
          await checkVendorSecurity(asset.id, asset.vendorWebsite);
        }

        toast.success('Asset updated successfully');
      }
    } catch (error) {
      console.error('Error saving asset:', error);
      toast.error('Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = async (key: string, value: any) => {
    // If dataTypesShared is changing, sync asset <-> data type links
    if (key === 'dataTypesShared' && asset) {
      const prevDataTypeIds = Array.isArray(asset.dataTypesShared) ? asset.dataTypesShared : [];
      const newDataTypeIds = Array.isArray(value) ? value : [];
      // Only sync if arrays are different
      if (
        prevDataTypeIds.length !== newDataTypeIds.length ||
        prevDataTypeIds.some(id => !newDataTypeIds.includes(id)) ||
        newDataTypeIds.some(id => !prevDataTypeIds.includes(id))
      ) {
        // Await but don't block UI
        console.log('[AssetDetail] Syncing data type assets:', { assetId: asset.id, prevDataTypeIds, newDataTypeIds });
        syncDataTypeAssets(asset.id, prevDataTypeIds, newDataTypeIds).catch(console.error);
      }
    }
    console.log('[AssetDetail] Field changed:', { key, value, currentAsset: asset });
    try {
      // Note: Vendor website security check is now handled in handleSave
      // to prevent excessive API calls during editing

      if (key === 'lastReviewDate' || key === 'nextReviewDate') {
        // Ensure it is a DATE not a string
        const dateValue = cleanDateAndTimeObject(value);
        value = dateValue;
        if (!(dateValue instanceof Date)) {
          value = new Date(dateValue);
        }

        // Add current time to the date
        value.setHours(new Date().getHours());
        value.setMinutes(new Date().getMinutes());
        value.setSeconds(new Date().getSeconds());
        value.setMilliseconds(new Date().getMilliseconds());
      }
      // Update local state
      setEditedValues(prev => ({
        ...prev,
        [key]: value
      }));

      setAsset(prev => ({
        ...prev,
        [key]: value
      }));

      // Add to changes object for eventual save
      setChanges(prev => ({
        ...(prev || {}),
        [key]: value
      }));

      // In create mode, don't close the field or save to backend yet
      if (isCreateMode) {
        return;
      }


      // For existing assets, close the field and save immediately
      setActiveField(null);

      // Show loading indicator
      toast.loading(`Saving ${key}...`);
      console.log(`Saving ${key}...`, value);

      if (!id) {
        toast.error('No asset ID provided');
        return;
      }

      // Get the latest asset to ensure we're working with fresh data
      const assetInstance = await getAsset(id);

      if (!assetInstance) {
        throw new Error('Could not find asset to update');
      }

      // Convert frontend changes to backend format
      const backendChanges: Partial<AssetRegister.AssetEntity> = {};

      // Map the specific field from frontend to backend model
      const backendKey = mapFrontendToBackendKey(key);
      backendChanges[backendKey] = value;

      // Update the asset via the service
      await updateAsset(id, backendChanges);

      // Update vendor score if needed
      if (key === 'vendorWebsite') {
        await updateAsset(id, {
          vendorWebsite: value,
          securityScore: asset.securityScore,
          lastUpguardCheck: {
            ...(asset.lastUpguardCheck || {}),
          },
          upguardSecurityScoreHistory: {
            ...(asset.upguardSecurityScoreHistory || {}),
          }
        });
      }

      // Sync asset-risk relationships if needed
      if (key === 'risks') {
        try {
          await syncAssetRiskLinks(id, assetInstance.entity.risks || [], value);
          toast.success('Asset-risk relationships synced successfully');
        } catch (error) {
          toast.error('Failed to sync asset-risk relationships');
        }
      }


      // Refresh the asset data, but preserve our current UI state
      const updatedAssetInstance = await getAsset(id);
      if (updatedAssetInstance) {
        setOriginalAssetEntity(updatedAssetInstance);

        // Important: Merge the updated field with the existing asset data
        // This ensures we don't lose any fields that might not be returned by the API
        setAsset(prevAsset => {
          // Start with the previous asset to preserve all existing fields
          const mergedAsset = { ...prevAsset };

          // Apply the specific field that was updated
          mergedAsset[key] = value;

          return mergedAsset;
        });

        // Refresh history
        const history = await getAssetHistory(id);
        // Sort history by date (newest first)
        const sortedHistory = [...history].sort((a, b) => {
          return new Date(cleanDateAndTimeObject(b.changedDate)).getTime() - new Date(cleanDateAndTimeObject(a.changedDate)).getTime();
        });
        console.log(sortedHistory);
        setAssetHistory(sortedHistory);
      }

      // Dismiss loading indicator
      toast.dismiss();

      // Show success message
      toast.success(`Asset ${key} updated successfully`);
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
      toast.error(`Failed to update ${key}`);
    }
  };

  return (
    <div className="page-container">
      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="spinner" />
        </div>
      )}

      {/* Historical Version Banner - Positioned under navigation */}
      {viewingHistoricalVersion && historicalAsset && selectedVersion !== null && (
        <div
          className={`w-full bg-yellow-50 dark:bg-yellow-900/90 border-b border-yellow-200 dark:border-yellow-800 py-2 transition-all duration-200 justify-center ${isBannerSticky ? 'fixed top-0 left-0 right-0 z-40 shadow-md' : 'hidden'
            }`}
          style={{
            backdropFilter: 'blur(4px)',
            marginTop: isBannerSticky ? '0' : '0',
          }}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faHistory} className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                  Viewing historical version from {renderDate(assetHistory[selectedVersion]?.changedDate, { dateStyle: 'long' })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleVersionSelect(null)}
                className="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded px-2 py-1"
              >
                Return to current version
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add padding to the body when banner is sticky */}
      {isBannerSticky && viewingHistoricalVersion && (
        <div className="h-10 w-full"></div>
      )}

      <section className="container" style={{ minHeight: '100%', minWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {!isReadOnly && (
          <div style={{ width: '100%', maxWidth: 1100, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <button className="back-button" onClick={navigateToAssetRegister}>
              <i className="fa-solid fa-chevron-left"></i> Back to {asset?.type === 'Supplier' ? 'Supplier' : 'Asset'} Register
            </button>
          </div>
        )}
        <div
          className="glass-card"
          style={{
            width: '100%',
            maxWidth: 1100,
            backgroundColor: theme.isDark ? '#18181b' : 'white',
            color: theme.isDark ? '#f3f4f6' : '#18181b',
            border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
            boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <div className="header-row">
            <div>
              <h2 className="title">{loading ? 'Loading...' : isCreateMode ? 'New Asset' : asset?.name}</h2>
              <div className="subtitle">{asset?.category}</div>
            </div>
            <div className="header-actions">
              {isEditing ? (
                <>
                  {isCreateMode && (
                    <>
                      <Button
                        className="primary-btn"
                        style={{
                          background: 'var(--primary)',
                          color: 'var(--primary-foreground)',
                          border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`
                        }}
                        onClick={handleSave}
                        disabled={saving || (!isCreateMode && Object.keys(changes || {}).length === 0)}
                      >
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Changes
                      </Button>
                      {/* <button
                        className="outline-btn"
                        onClick={() => {
                          setIsEditing(false);
                          setChanges({});
                        }}
                        disabled={saving}
                      >
                        Cancel
                      </button> */}
                    </>
                  )}
                </>
              ) : (
                <>
                  {isCreateMode && !isReadOnly && (
                    <>
                      <button
                        className="primary-btn"
                        style={{
                          background: colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary,
                          color: theme.isDark ? '#fff' : '#18181b',
                          border: `1px solid ${colorValues[theme.primaryColor]?.primary ?? colorValues.default.primary}`
                        }}
                        onClick={() => setIsEditing(true)}
                        disabled={loading}
                      >
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </button>
                    </>
                  )}
                  {/* Only show History button if asset is loaded and not in edit mode and not in create mode */}
                  {asset && !isEditing && !isCreateMode && (
                    <div className="secondary-actions flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      {!isReadOnly && (
                        <Button 
                          variant='destructive' 
                          onClick={() => setShowDeleteDialog(true)}
                          className="w-full sm:w-auto justify-center"
                        >
                          Delete
                        </Button>
                      )}
                      <Button 
                        variant='outline' 
                        onClick={() => setIsVendorScoreHistoryOpen(true)}
                        className="w-full sm:w-auto justify-center"
                      >
                        <History className="mr-2 h-4 w-4" />
                        <span className="whitespace-nowrap">Vendor Score History</span>
                      </Button>
                      <Button 
                        variant='secondary' 
                        onClick={() => setShowHistory(!showHistory)}
                        className="w-full sm:w-auto justify-center"
                      >
                        <FontAwesomeIcon icon={showHistory ? faEyeSlash : faHistory} className="mr-2" />
                        <span>{showHistory ? 'Hide History' : 'History'}</span>
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Historical Version Banner */}
          {viewingHistoricalVersion && historicalAsset && selectedVersion !== null && (
            <div className={`bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-6 rounded-r transition-all duration-200 ${isBannerSticky ? 'hidden' : ''}`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faHistory} className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    You are viewing this asset as it was on {renderDate(assetHistory[selectedVersion]?.changedDate, { dateStyle: 'long' })}.
                    <button
                      type="button"
                      onClick={() => handleVersionSelect(null)}
                      className="ml-2 font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-600 dark:hover:text-yellow-200 underline"
                    >
                      Return to current version
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Banner reference point for scroll detection */}
          <div ref={bannerRef} style={{ position: 'absolute', top: '20px', visibility: 'hidden' }} />

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading asset details...</span>
            </div>
          ) : asset ? (
            <>
              {/* Dynamically render all sections and fields */}
              {Array.from(new Set(assetFieldsConfig.map(f => f.section))).map(section => (
                <div className="asset-section" key={section}>
                  <h3 className="section-title">{section}</h3>
                  <div className="property-grid">
                    {assetFieldsConfig.filter(f => f.section === section).map(field => (
                      <div
                        key={field.key}
                        className={
                          field.fullWidth
                            ? "property-item full-width"
                            : "property-item"
                        }
                      >
                        <EditableField
                          isCreateMode={isCreateMode}
                          label={field.label}
                          value={displayAsset[field.key]}
                          type={field.type}
                          options={field.options}
                          // Make field read-only if specified in config
                          // Combined readOnly logic - field is read-only if isReadOnly is true OR field.readOnly is true
                          readOnly={isReadOnly || field.readOnly || false}
                          // Security score props for vendor website
                          showSecurityScore={field.showSecurityScore}
                          securityScore={securityScore}
                          isCheckingSecurity={isCheckingSecurity}
                          securityCheckError={securityCheckError}
                          onSecurityCheck={field.showSecurityScore ? (assetID: string, url: string) => {
                            checkVendorSecurity(assetID, url);
                          } : undefined}
                          // Disable editing when viewing historical versions, if field is read-only, or in read-only mode
                          editing={!isReadOnly && !viewingHistoricalVersion && !field.readOnly && (isCreateMode || isEditing || activeField === field.key)}
                          // Different onSave behavior based on create vs edit mode
                          onSave={value => {
                            if (isCreateMode) {
                              // Just update local state for create mode
                              setAsset(prev => ({
                                ...prev,
                                [field.key]: value
                              }));

                              // Track changes for final save
                              setChanges(prev => ({
                                ...(prev || {}),
                                [field.key]: value
                              }));
                            } else {
                              // Normal field save with API call for edit mode
                              handleFieldChange(field.key, value);
                            }
                          }}
                          // Only use setEditing in non-create mode
                          setEditing={isCreateMode ? undefined : (editing) => {
                            if (editing && !isEditing) {
                              setActiveField(field.key);
                            } else if (!editing && activeField === field.key) {
                              setActiveField(null);
                            }
                          }}
                          // In create mode, hide the individual save button
                          hideActionButton={isCreateMode}
                          // Pass hidden condition from the field config
                          hidden={field.hidden}
                          // Pass all current form values to enable conditional field hiding
                          formValues={asset}
                          data-field-key={field.key}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="p-4 text-center text-red-500 bg-red-50 dark:bg-red-900/20 rounded-md my-4">
              Asset not found or error loading asset details
            </div>
          )}
          {/* Vendor Security Score Chart */}
          {shouldShowVendorChart && vendorScoreHistory.length > 0 && (
            <div className="mb-6">
              <VendorScoreChart history={vendorScoreHistory} domain={vendorHostname} />
            </div>
          )}
          {/* History Panel (toggleable) */}
          {showHistory && (
            <div className="bg-white/85 dark:bg-neutral-900/80 backdrop-blur-md rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-6 mb-6 overflow-hidden"
              id="historyPanel"
              style={{ marginTop: 24 }}
              ref={historyPanelRef}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Version History</h3>
                <div className="flex gap-2">
                  <Tabs>
                    <TabsList className="h-8">
                      <TabsTrigger
                        value="all"
                        className="text-xs px-3 py-1 h-7"
                        onClick={() => setActiveTab('all')}
                      >
                        All Changes
                      </TabsTrigger>
                      {/* Hide Restore Version tab when in read-only mode */}
                      {!isReadOnly && (
                        <TabsTrigger
                          value="restore"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => setActiveTab('restore')}
                        >
                          Restore Version
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>
                </div>
              </div>

              <div className="flex flex-col gap-4 max-h-[500px] overflow-y-auto pr-2 -mr-2">
                {assetHistory.length > 0 ? (
                  assetHistory.map((item, index) => {
                    const formattedDate = renderDate(item.changedDate, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    });
                    const isCurrent = index === 0 && selectedVersion === null;
                    const isSelected = selectedVersion === index;

                    return (
                      <Card
                        key={index}
                        className={`relative overflow-visible transition-all ${isSelected ? 'ring-2 ring-primary' : ''} ${isCurrent ? 'border-l-4 border-l-green-500' : ''
                          }`}
                      >
                        <CardHeader className="p-4 pb-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-sm font-medium flex items-center gap-2">
                                {isCurrent ? (
                                  <Badge variant="outline" className="text-xs">Current</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">
                                    Version {assetHistory.length - index}
                                  </Badge>
                                )}
                                {userIdToName[item.changedBy]}
                              </CardTitle>
                              <CardDescription className="text-xs mt-1">
                                {formattedDate}
                              </CardDescription>
                            </div>
                            {!isCurrent && activeTab === 'restore' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreClick(item, index)}
                                disabled={isRestoring}
                                className="text-xs h-7"
                              >
                                {isRestoring && selectedVersion === index ? (
                                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-3 h-3 mr-1.5" />
                                )}
                                Restore
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="p-4 pt-0 overflow-visible">
                          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                            {Object.entries(item.changes).length} {Object.entries(item.changes).length === 1 ? 'change' : 'changes'} made
                          </div>

                          <div className="space-y-3">
                            {Object.entries(item.changes).slice(0, 3).map(([field, changeValue], i) => {
                              const change = changeValue as {
                                elementName: string;
                                oldValue: string | null | undefined;
                                newValue: string | null | undefined;
                              };

                              // List of fields that contain user IDs and should be resolved to names
                              const userIdFields = ['owner', 'assetOwner', 'reviewedBy', 'changedBy', 'createdBy', 'updatedBy'];

                              const renderField = (key: string, value: string | null | undefined) => {
                                if (value === null || value === undefined) return 'None';

                                // Handle date fields
                                if (key === 'dateAdded' || key === 'nextReviewDate' || key === 'lastReviewDate') {
                                  return toLocalDateString(new Date(value));
                                }

                                // Handle user ID fields
                                const normalizedKey = key.toLowerCase();
                                if (userIdFields.some(field => normalizedKey.includes(field.toLowerCase())) && typeof value === 'string') {
                                  return userIdToName[value] || value; // Return mapped name or original value if not found
                                }

                                return value.toString();
                              };

                              return (
                                <div key={i} className="text-xs">
                                  <div className="font-medium text-gray-700 dark:text-gray-200">
                                    {change.elementName}
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 mt-1">
                                    <div className="text-gray-500 dark:text-gray-400 line-through">
                                      {renderField(change.elementName, change.oldValue)}
                                    </div>
                                    <div className="text-primary font-medium">
                                      {renderField(change.elementName, change.newValue)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {Object.entries(item.changes).length > 3 && (
                              <div className="text-xs text-primary/80 dark:text-primary/60">
                                +{Object.entries(item.changes).length - 3} more changes
                              </div>
                            )}
                          </div>

                          {activeTab !== 'restore' && (
                            <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7 px-2"
                                onClick={() => handleVersionSelect(index)}
                              >
                                <FontAwesomeIcon icon={faHistory} className="w-3 h-3 mr-1.5" />
                                View Asset on this Date
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <FontAwesomeIcon icon={faHistory} className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">No version history available</p>
                    <p className="text-sm text-gray-400 mt-1">Changes will appear here when you update this asset</p>
                  </div>
                )}
              </div>

              {assetHistory.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
                  <p>Showing {Math.min(assetHistory.length, 5)} of {assetHistory.length} versions</p>
                </div>
              )}
            </div>
          )}
        </div>
        {/* MCM Mappings Card - identical second card */}
        <MCMMappingsCard
          tenancyId={tenancyId}
          isReadOnly={isReadOnly || viewingHistoricalVersion}
          controlIds={assetControlIds}
          defaultEntityLink={{
            id: asset?.id || asset?.assetID,
            ref: asset?.id || asset?.assetID,
            title: asset?.name || asset?.componentId || (asset?.id || ''),
            domain: 'Asset',
            relation: 'implements',
          }}
          onEnsureControlLinked={async (controlId) => {
            await addMcmControlToAsset(controlId);
          }}
        />
        {!isReadOnly && (
          <div style={{ width: '100%', maxWidth: 1100, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <button className="back-button" onClick={navigateToAssetRegister}>
              <i className="fa-solid fa-chevron-left"></i> Back to {asset?.type === 'Supplier' ? 'Supplier' : 'Asset'} Register
            </button>
          </div>
        )}
      </section>
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle>Delete Asset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this Asset? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Asset
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle>Restore Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to restore this document? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The document will be restored to the version you selected.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRestoreDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await handleRestoreVersion();
                setShowRestoreDialog(false);
                setVersionToRestore(null);
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Restore Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Score History Dialog */}
      <Dialog open={isVendorScoreHistoryOpen} onOpenChange={setIsVendorScoreHistoryOpen}>
        <DialogContent className="max-w-3xl bg-card">
          <DialogHeader>
            <DialogTitle>Vendor Score History</DialogTitle>
            <DialogDescription>
              Review historical vendor security scores and UpGuard check details for this asset.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Card>
              <CardHeader className="space-y-1">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Latest Security Score</CardTitle>
                    <CardDescription>
                      Last checked {latestCheckDisplay}
                    </CardDescription>
                  </div>
                  <Badge className={getSecurityScoreColor(latestScoreData.normalized ?? null)}>
                    {latestScoreData.display}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <div className="flex flex-col gap-1">
                  <div>
                    <span className="font-medium text-foreground">Vendor: </span>
                    {asset?.vendorWebsite || 'Not provided'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Monitoring Domain: </span>
                    {monitoringDomain || 'Not available'}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-foreground">Historical Checks</h3>
                <Badge variant="outline">Most recent first</Badge>
              </div>
              {upguardHistoryEntries.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 text-left">Check Date</th>
                        <th className="px-4 py-2 text-left">Score</th>
                        <th className="px-4 py-2 text-left">Domain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upguardHistoryEntries.map((entry) => (
                        <tr key={entry.key} className="border-t border-border hover:bg-muted/40">
                          <td className="px-4 py-2">{entry.displayDate || 'Unknown'}</td>
                          <td className="px-4 py-2 font-medium text-foreground">{entry.score || 'N/A'}</td>
                          <td className="px-4 py-2 text-muted-foreground">{entry.domain || vendorHostname || 'Unknown'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
                  <Shield className="h-8 w-8 text-muted mb-2" />
                  <p>No historical vendor score data available.</p>
                  <p className="text-xs">Run a security check once vendor website details are saved to build history.</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex justify-end">
            <Button variant="outline" onClick={() => setIsVendorScoreHistoryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetPage;
